import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import { default as GridLegacy } from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import api from '../services/api';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010';

interface Role {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface Department {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface EmploymentType {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface Team {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface UserStatus {
  value: string;
  label: string;
  color: string;
  bgColor: string;
}

interface Organization {
  id: number;
  name: string;
  parent_organization_id: number | null;
}

interface AttributionId {
  id: number;
  attribution_id: string;
  is_primary: boolean;
  created_at: string;
}

interface User {
  id: number;
  username: string;
  email: string | null;
  pin: string;
  attribution_id: string;
  attribution_ids?: AttributionId[];
  sales_tracker_id: string | null;
  role: string;
  permission_group_id: number | null;
  department: string;
  status: string;
  organizations: Organization[];
  user_management_access: boolean;
  photo_url?: string | null;
  city?: string | null;
  cell_phone?: string | null;
  business_phone?: string | null;
  business_direct_line?: string | null;
  birthday?: string | null;
  sms_capabilities?: boolean;
  start_date?: string | null;
  termination_date?: string | null;
  telegram_id?: string | null;
  notes?: string | null;
  tshirt_size?: string | null;
  employment_type?: string;
  team?: string | null;
}

interface PermissionGroup {
  id: number;
  name: string;
  description: string;
  legacy_role_mapping: string | null;
  is_system_group: boolean;
}

interface FormData {
  username: string;
  email: string;
  pin: string;
  attribution_id: string;
  sales_tracker_id: string;
  role: string;
  permission_group_id: number | null;
  department: string;
  status: string;
  organizations: number[];
  user_management_access: boolean;
  photo_url: string;
  city: string;
  cell_phone: string;
  business_phone: string;
  business_direct_line: string;
  birthday: string;
  sms_capabilities: boolean;
  start_date: string;
  termination_date: string;
  telegram_id: string;
  notes: string;
  tshirt_size: string;
  employment_type: string;
  team: string;
}

const ROLES: Role[] = [
  { value: 'superuser', label: 'Super User', color: '#d32f2f', bgColor: '#ffebee' },
  { value: 'manager', label: 'Manager', color: '#f57c00', bgColor: '#fff3e0' },
  { value: 'user', label: 'User', color: '#1976d2', bgColor: '#e3f2fd' }
];

const DEPARTMENTS: Department[] = [
  { value: 'Sales', label: 'Sales', color: '#1976d2', bgColor: '#e3f2fd' },
  { value: 'Customer Service', label: 'Customer Service', color: '#7b1fa2', bgColor: '#f3e5f5' },
  { value: 'Support', label: 'Support', color: '#0288d1', bgColor: '#e1f5fe' },
  { value: 'Management', label: 'Management', color: '#f57c00', bgColor: '#fff3e0' }
];

const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

const TEAMS: Team[] = [
  { value: 'Pharm Animals', label: 'Pharm Animals', color: '#7DF9FF', bgColor: '#000000' },
  { value: 'Cali Crushers', label: 'Cali Crushers', color: '#000000', bgColor: '#DAA520' },
  { value: 'Goal Diggers', label: 'Goal Diggers', color: '#ffffff', bgColor: '#4caf50' },
  { value: 'Weekend Warriors', label: 'Weekend Warriors', color: '#ffffff', bgColor: '#ff9800' }
];

const USER_STATUSES: UserStatus[] = [
  { value: 'Active', label: 'Active', color: '#ffffff', bgColor: '#4caf50' },
  { value: 'Inactive', label: 'Inactive', color: '#ffffff', bgColor: '#757575' },
  { value: 'Offer Sent', label: 'Offer Sent', color: '#ffffff', bgColor: '#2196F3' },
  { value: 'Needs Onboarding', label: 'Needs Onboarding', color: '#ffffff', bgColor: '#ff9800' },
  { value: 'Onboarding', label: 'Onboarding', color: '#ffffff', bgColor: '#4B88A2' }
];

function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'username' | 'organizations' | 'status'>('username');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState('All');
  const [organizationFilter, setOrganizationFilter] = useState('All');
  const [permissionGroupFilter, setPermissionGroupFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  
  // Attribution IDs state
  const [attributionIds, setAttributionIds] = useState<AttributionId[]>([]);
  const [newAttributionId, setNewAttributionId] = useState('');
  const [showAddAttrId, setShowAddAttrId] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    pin: '',
    attribution_id: '',
    sales_tracker_id: '',
    role: 'user',
    permission_group_id: null,
    department: 'Sales',
    status: 'Active',
    organizations: [],
    user_management_access: false,
    photo_url: '',
    city: '',
    cell_phone: '',
    business_phone: '',
    business_direct_line: '',
    birthday: '',
    sms_capabilities: false,
    start_date: '',
    termination_date: '',
    telegram_id: '',
    notes: '',
    tshirt_size: '',
    employment_type: 'W9',
    team: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    fetchPermissionGroups();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/user-management/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      showSnackbar('Failed to fetch users', 'error');
      console.error('Error fetching users:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setOrganizations(data.organizations.filter((org: Organization) => org.parent_organization_id !== null));
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchPermissionGroups = async () => {
    try {
      const groups = await api.getAllPermissionGroups();
      setPermissionGroups(groups);
    } catch (error) {
      console.error('Error fetching permission groups:', error);
      showSnackbar('Failed to fetch permission groups', 'error');
    }
  };

  const fetchAttributionIds = async (userId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/attribution-ids/users/${userId}/attribution-ids`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAttributionIds(data.attributionIds || []);
      }
    } catch (error) {
      console.error('Error fetching attribution IDs:', error);
      setAttributionIds([]);
    }
  };

  const handleAddAttributionId = async () => {
    if (!newAttributionId.trim()) {
      showSnackbar('Please enter an attribution ID', 'error');
      return;
    }

    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/attribution-ids/users/${selectedUser.id}/attribution-ids`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            attribution_id: newAttributionId.trim(),
            is_primary: attributionIds.length === 0
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        showSnackbar('Attribution ID added successfully', 'success');
        fetchAttributionIds(selectedUser.id);
        setNewAttributionId('');
        setShowAddAttrId(false);
      } else {
        showSnackbar(data.error || 'Failed to add attribution ID', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to add attribution ID', 'error');
      console.error('Error adding attribution ID:', error);
    }
  };

  const handleSetPrimaryAttributionId = async (attrId: number) => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/attribution-ids/users/${selectedUser.id}/attribution-ids/${attrId}`,
        {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({ is_primary: true })
        }
      );

      const data = await response.json();
      if (data.success) {
        showSnackbar('Primary attribution ID updated', 'success');
        fetchAttributionIds(selectedUser.id);
        fetchUsers();
      } else {
        showSnackbar(data.error || 'Failed to update attribution ID', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to update attribution ID', 'error');
      console.error('Error updating attribution ID:', error);
    }
  };

  const handleDeleteAttributionId = async (attrId: number, attrValue: string) => {
    if (!window.confirm(`Are you sure you want to delete attribution ID "${attrValue}"?`)) {
      return;
    }

    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/attribution-ids/users/${selectedUser.id}/attribution-ids/${attrId}`,
        { 
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        showSnackbar('Attribution ID deleted successfully', 'success');
        fetchAttributionIds(selectedUser.id);
        fetchUsers();
      } else {
        showSnackbar(data.error || 'Failed to delete attribution ID', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to delete attribution ID', 'error');
      console.error('Error deleting attribution ID:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = async (user: User | null = null) => {
    if (user) {
      setEditMode(true);
      setSelectedUser(user);
      
      await fetchAttributionIds(user.id);
      
      const formatDateForInput = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (e) {
          return '';
        }
      };
      
      setFormData({
        username: user.username,
        email: user.email || '',
        pin: '', // the API never returns PINs; blank = keep current PIN
        attribution_id: user.attribution_id,
        sales_tracker_id: user.sales_tracker_id || '',
        role: user.role,
        permission_group_id: user.permission_group_id,
        department: user.department || 'Sales',
        status: user.status || 'Active',
        organizations: user.organizations.map(org => org.id),
        user_management_access: user.user_management_access || false,
        photo_url: user.photo_url || '',
        city: user.city || '',
        cell_phone: user.cell_phone || '',
        business_phone: user.business_phone || '',
        business_direct_line: user.business_direct_line || '',
        birthday: formatDateForInput(user.birthday),
        sms_capabilities: user.sms_capabilities || false,
        start_date: formatDateForInput(user.start_date),
        termination_date: formatDateForInput(user.termination_date),
        telegram_id: user.telegram_id || '',
        notes: user.notes || '',
        tshirt_size: user.tshirt_size || '',
        employment_type: user.employment_type || 'W9',
        team: user.team || ''
      });
    } else {
      setEditMode(false);
      setSelectedUser(null);
      setAttributionIds([]);
      setFormData({
        username: '',
        email: '',
        pin: '',
        attribution_id: '',
        sales_tracker_id: '',
        role: 'user',
        permission_group_id: null,
        department: 'Sales',
        status: 'Active',
        organizations: [],
        user_management_access: false,
        photo_url: '',
        city: '',
        cell_phone: '',
        business_phone: '',
        business_direct_line: '',
        birthday: '',
        sms_capabilities: false,
        start_date: '',
        termination_date: '',
        telegram_id: '',
        notes: '',
        tshirt_size: '',
        employment_type: 'W9',
        team: ''
      });
    }
    setOpenDialog(true);
    setShowAddAttrId(false);
    setNewAttributionId('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedUser(null);
    setAttributionIds([]);
    setShowAddAttrId(false);
    setNewAttributionId('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name!]: value });
  };

  const handleBooleanSelectChange = (e: SelectChangeEvent<boolean>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name!]: value });
  };

  const handleOrganizationChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    const orgIds = typeof value === 'string' ? value.split(',').map(Number) : value;
    setFormData({ ...formData, organizations: orgIds });
  };

  const handleSubmit = async () => {
    try {
      const url = editMode 
        ? `${API_BASE_URL}/api/user-management/users/${selectedUser?.id}`
        : `${API_BASE_URL}/api/user-management/users`;
      
      const method = editMode ? 'PUT' : 'POST';

      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        showSnackbar(data.message, 'success');
        fetchUsers();
        if (!editMode) {
          handleCloseDialog();
        }
      } else {
        showSnackbar(data.error || 'Failed to save user', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to save user', 'error');
      console.error('Error saving user:', error);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/user-management/users/${userId}`,
        { 
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        showSnackbar(data.message, 'success');
        fetchUsers();
      } else {
        showSnackbar(data.error || 'Failed to delete user', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to delete user', 'error');
      console.error('Error deleting user:', error);
    }
  };

  const getStatusConfig = (status: string): UserStatus => {
    const statusObj = USER_STATUSES.find(s => s.value === status);
    return statusObj || USER_STATUSES[0];
  };

  const getDepartmentConfig = (department: string): Department => {
    const deptObj = DEPARTMENTS.find(d => d.value === department);
    return deptObj || DEPARTMENTS[0];
  };

  const filteredAndSortedUsers = users
    .filter(user => {
      if (permissionGroupFilter === 'Unassigned' && user.permission_group_id !== null) {
        return false;
      } else if (permissionGroupFilter !== 'All' && permissionGroupFilter !== 'Unassigned') {
        const groupId = parseInt(permissionGroupFilter);
        if (user.permission_group_id !== groupId) {
          return false;
        }
      }
      
      if (organizationFilter !== 'All') {
        const hasOrg = user.organizations.some(org => org.id === parseInt(organizationFilter));
        if (!hasOrg) return false;
      }
      
      if (departmentFilter !== 'All' && (user.department || 'Sales') !== departmentFilter) {
        return false;
      }
      
      if (statusFilter !== 'All' && user.status !== statusFilter) {
        return false;
      }
      
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const usernameMatch = user.username.toLowerCase().includes(query);
      const emailMatch = user.email && user.email.toLowerCase().includes(query);
      const orgMatch = user.organizations.some(org => 
        org.name.toLowerCase().includes(query)
      );
      return usernameMatch || emailMatch || orgMatch;
    })
    .sort((a, b) => {
      let compareValue = 0;
      
      if (sortField === 'username') {
        compareValue = a.username.localeCompare(b.username);
      } else if (sortField === 'organizations') {
        const aOrgs = a.organizations.map(o => o.name).join(', ');
        const bOrgs = b.organizations.map(o => o.name).join(', ');
        compareValue = aOrgs.localeCompare(bOrgs);
      } else if (sortField === 'status') {
        compareValue = (a.status || 'Active').localeCompare(b.status || 'Active');
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

  const handleSort = (field: 'username' | 'organizations' | 'status') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const statusCounts = filteredAndSortedUsers.reduce<Record<string, number>>((acc, user) => {
    const status = user.status || 'Active';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const handleExportCSV = () => {
    if (filteredAndSortedUsers.length === 0) {
      showSnackbar('No users to export', 'warning');
      return;
    }

    const csvHeaders = ['Username', 'Email'];
    const csvRows = filteredAndSortedUsers.map(user => [
      user.username,
      user.email || ''
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(cell => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `users_export_${date}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSnackbar(`Exported ${filteredAndSortedUsers.length} users to CSV`, 'success');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#4B88A2',
            '&:hover': { bgcolor: '#3d7088' },
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Add User
        </Button>
      </Box>

      <GridLegacy container spacing={2} sx={{ mb: 3 }}>
        {USER_STATUSES.map((status) => {
          const count = statusCounts[status.value] || 0;
          return (
            <GridLegacy size={{ xs: 12, sm: 6, md: 2.4 }} key={status.value}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: statusFilter === status.value ? `2px solid ${status.bgColor}` : '1px solid #e0e0e0',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}
                onClick={() => setStatusFilter(statusFilter === status.value ? 'All' : status.value)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: status.bgColor, mb: 0.5 }}>
                    {count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                    {status.label}
                  </Typography>
                </CardContent>
              </Card>
            </GridLegacy>
          );
        })}
      </GridLegacy>

      <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
        <GridLegacy container spacing={2} alignItems="center">
          <GridLegacy size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search by username, email, or organization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#4B88A2' }} />
              }}
              sx={{ bgcolor: 'white' }}
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth sx={{ bgcolor: 'white' }}>
              <InputLabel>Organization</InputLabel>
              <Select
                value={organizationFilter}
                onChange={(e) => setOrganizationFilter(e.target.value)}
                label="Organization"
              >
                <MenuItem value="All">All Organizations</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id.toString()}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridLegacy>
          <GridLegacy size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth sx={{ bgcolor: 'white' }}>
              <InputLabel>Permission Group</InputLabel>
              <Select
                value={permissionGroupFilter}
                onChange={(e) => setPermissionGroupFilter(e.target.value)}
                label="Permission Group"
              >
                <MenuItem value="All">All Groups</MenuItem>
                <MenuItem value="Unassigned">Unassigned</MenuItem>
                {permissionGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id.toString()}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridLegacy>
          <GridLegacy size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth sx={{ bgcolor: 'white' }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                label="Department"
              >
                <MenuItem value="All">All Departments</MenuItem>
                {DEPARTMENTS.map((dept) => (
                  <MenuItem key={dept.value} value={dept.value}>
                    {dept.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridLegacy>
          <GridLegacy size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth sx={{ bgcolor: 'white' }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="All">All Statuses</MenuItem>
                {USER_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridLegacy>
        </GridLegacy>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
          Showing {filteredAndSortedUsers.length} user{filteredAndSortedUsers.length !== 1 ? 's' : ''}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCSV}
          disabled={filteredAndSortedUsers.length === 0}
          sx={{
            borderColor: '#4B88A2',
            color: '#4B88A2',
            '&:hover': { borderColor: '#3d7088', bgcolor: '#e0f2f7' },
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Export to CSV
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#2C3E50' }}>
            <TableRow>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none', color: 'white', fontWeight: 600 }}
                onClick={() => handleSort('username')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Username
                  {sortField === 'username' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Attribution IDs</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Sales Tracker ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Permission Group</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Department</TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none', color: 'white', fontWeight: 600 }}
                onClick={() => handleSort('organizations')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Organizations
                  {sortField === 'organizations' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ cursor: 'pointer', userSelect: 'none', color: 'white', fontWeight: 600 }}
                onClick={() => handleSort('status')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedUsers.map((user, index) => {
              const statusConfig = getStatusConfig(user.status);
              const deptConfig = getDepartmentConfig(user.department);
              
              return (
                <TableRow 
                  key={user.id}
                  onClick={() => handleOpenDialog(user)}
                  sx={{
                    bgcolor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#e3f2fd',
                      transform: 'scale(1.002)',
                      boxShadow: 1
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{user.username}</TableCell>
                  <TableCell>{user.email || '-'}</TableCell>
                  <TableCell>
                    {user.attribution_ids && user.attribution_ids.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.attribution_ids.map((attr) => (
                          <Chip
                            key={attr.id}
                            label={attr.attribution_id}
                            size="small"
                            sx={{
                              fontFamily: 'monospace',
                              bgcolor: '#e0e0e0',
                              fontWeight: 400
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Chip 
                        label={user.attribution_id} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{user.sales_tracker_id || '-'}</TableCell>
                  <TableCell>
                    {user.permission_group_id ? (
                      <Chip 
                        label={permissionGroups.find(g => g.id === user.permission_group_id)?.name || 'Unknown'} 
                        size="small" 
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 600,
                          border: '1px solid #1976d2'
                        }}
                      />
                    ) : (
                      <Chip 
                        label="Not Assigned" 
                        size="small" 
                        sx={{
                          bgcolor: '#f5f5f5',
                          color: '#757575',
                          fontWeight: 500
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.department || 'Sales'} 
                      size="small" 
                      sx={{
                        bgcolor: deptConfig.bgColor,
                        color: deptConfig.color,
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {user.organizations.length > 0 ? (
                      user.organizations.map(org => (
                        <Chip 
                          key={org.id} 
                          icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                          label={org.name} 
                          size="small" 
                          sx={{ 
                            mr: 0.5, 
                            mb: 0.5,
                            bgcolor: '#e0f2f7',
                            color: '#2C3E50',
                            fontWeight: 500
                          }} 
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={statusConfig.label} 
                      size="small" 
                      sx={{
                        bgcolor: statusConfig.bgColor,
                        color: statusConfig.color,
                        fontWeight: 600,
                        minWidth: 120
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(user);
                      }}
                      size="small"
                      sx={{ color: '#4B88A2', '&:hover': { bgcolor: '#e0f2f7' } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user.id);
                      }}
                      size="small"
                      sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2C3E50', color: 'white', fontWeight: 600 }}>
          {editMode ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <GridLegacy container spacing={2} sx={{ pt: 2 }}>
            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </GridLegacy>
            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </GridLegacy>
            
            {/* Attribution IDs Section - Only show when editing */}
            {editMode && (
              <>
                <GridLegacy size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#4B88A2', fontWeight: 600 }}>
                      Attribution IDs ({attributionIds.length})
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setShowAddAttrId(!showAddAttrId)}
                      sx={{ textTransform: 'none' }}
                    >
                      Add ID
                    </Button>
                  </Box>
                  
                  {showAddAttrId && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <GridLegacy container spacing={2} alignItems="center">
                        <GridLegacy size={{ xs: 12, sm: 8 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="New Attribution ID"
                            value={newAttributionId}
                            onChange={(e) => setNewAttributionId(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddAttributionId();
                              }
                            }}
                          />
                        </GridLegacy>
                        <GridLegacy size={{ xs: 12, sm: 4 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={handleAddAttributionId}
                            sx={{ bgcolor: '#4B88A2' }}
                          >
                            Add
                          </Button>
                        </GridLegacy>
                      </GridLegacy>
                    </Box>
                  )}

                  <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <List dense>
                      {attributionIds.length === 0 ? (
                        <ListItem>
                          <MuiListItemText 
                            primary="No attribution IDs yet" 
                            secondary="Click 'Add ID' to create one"
                          />
                        </ListItem>
                      ) : (
                        attributionIds.map((attr) => (
                          <ListItem
                            key={attr.id}
                            secondaryAction={
                              <Box>
                                {!attr.is_primary && (
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleSetPrimaryAttributionId(attr.id)}
                                    title="Set as primary"
                                  >
                                    <StarBorderIcon fontSize="small" />
                                  </IconButton>
                                )}
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleDeleteAttributionId(attr.id, attr.attribution_id)}
                                  sx={{ color: '#d32f2f' }}
                                  disabled={attributionIds.length === 1}
                                  title={attributionIds.length === 1 ? "Cannot delete last attribution ID" : "Delete"}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            }
                          >
                            <MuiListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontFamily: 'monospace', fontWeight: attr.is_primary ? 600 : 400 }}
                                  >
                                    {attr.attribution_id}
                                  </Typography>
                                  {attr.is_primary && (
                                    <Chip
                                      icon={<StarIcon sx={{ fontSize: 14 }} />}
                                      label="Primary"
                                      size="small"
                                      sx={{
                                        bgcolor: '#4caf50',
                                        color: 'white',
                                        fontWeight: 600,
                                        height: 20
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={`Added ${new Date(attr.created_at).toLocaleDateString()}`}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Paper>
                  <Divider sx={{ mt: 2 }} />
                </GridLegacy>
              </>
            )}

            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={editMode ? 'New PIN (leave blank to keep current)' : 'PIN'}
                name="pin"
                value={formData.pin}
                onChange={handleInputChange}
                required={!editMode}
                autoComplete="off"
                helperText={editMode ? 'Current PINs cannot be viewed. Enter a value only to reset it.' : 'Minimum 4 characters'}
                inputProps={{ maxLength: 6 }}
              />
            </GridLegacy>
            {!editMode && (
              <GridLegacy size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Attribution ID"
                  name="attribution_id"
                  value={formData.attribution_id}
                  onChange={handleInputChange}
                  required
                />
              </GridLegacy>
            )}
            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Sales Tracker ID"
                name="sales_tracker_id"
                value={formData.sales_tracker_id}
                onChange={handleInputChange}
              />
            </GridLegacy>
            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  label="Status"
                >
                  {USER_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      <Chip 
                        label={status.label} 
                        size="small"
                        sx={{
                          bgcolor: status.bgColor,
                          color: status.color,
                          fontWeight: 600
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridLegacy>
            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Permission Group</InputLabel>
                <Select
                  name="permission_group_id"
                  value={formData.permission_group_id || ''}
                  onChange={(e) => setFormData({ ...formData, permission_group_id: e.target.value ? Number(e.target.value) : null })}
                  label="Permission Group"
                >
                  <MenuItem value="">
                    <em>Not assigned</em>
                  </MenuItem>
                  {permissionGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridLegacy>
            <GridLegacy size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleSelectChange}
                  label="Department"
                >
                  {DEPARTMENTS.map((dept) => (
                    <MenuItem key={dept.value} value={dept.value}>
                      {dept.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridLegacy>
            <GridLegacy size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Organizations</InputLabel>
                <Select
                  multiple
                  value={formData.organizations}
                  onChange={handleOrganizationChange}
                  input={<OutlinedInput label="Organizations" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const org = organizations.find(o => o.id === value);
                        return <Chip key={value} label={org?.name} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      <Checkbox checked={formData.organizations.indexOf(org.id) > -1} />
                      <ListItemText primary={org.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridLegacy>

            {/* New Profile Fields Section */}
            {editMode && (
              <>
                <GridLegacy size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ color: '#4B88A2', fontWeight: 600 }}>
                    Additional Profile Information
                  </Typography>
                </GridLegacy>

                <GridLegacy size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Photo URL"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/photo.jpg"
                    helperText="URL to user's profile photo"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="City (Location)"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Cell Phone"
                    name="cell_phone"
                    value={formData.cell_phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Business Phone"
                    name="business_phone"
                    value={formData.business_phone}
                    onChange={handleInputChange}
                    placeholder="(555) 987-6543"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Business Direct Line"
                    name="business_direct_line"
                    value={formData.business_direct_line}
                    onChange={handleInputChange}
                    placeholder="(555) 111-2222"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Birthday"
                    name="birthday"
                    type="date"
                    value={formData.birthday}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    helperText="Employment start date"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Termination Date"
                    name="termination_date"
                    type="date"
                    value={formData.termination_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    helperText="Employment termination date (if applicable)"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>SMS Capabilities</InputLabel>
                    <Select
                      name="sms_capabilities"
                      value={formData.sms_capabilities}
                      onChange={handleBooleanSelectChange}
                      label="SMS Capabilities"
                    >
                      <MenuItem value={true as any}>Yes</MenuItem>
                      <MenuItem value={false as any}>No</MenuItem>
                    </Select>
                  </FormControl>
                </GridLegacy>

                <GridLegacy size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ color: '#4B88A2', fontWeight: 600 }}>
                    Additional Information
                  </Typography>
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Telegram ID"
                    name="telegram_id"
                    type="number"
                    value={formData.telegram_id}
                    onChange={handleInputChange}
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    placeholder="123456789"
                    helperText="Numeric Telegram user ID for bot integration"
                  />
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>T-Shirt Size</InputLabel>
                    <Select
                      name="tshirt_size"
                      value={formData.tshirt_size}
                      onChange={handleSelectChange}
                      label="T-Shirt Size"
                    >
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      {TSHIRT_SIZES.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridLegacy>

                <GridLegacy size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Team</InputLabel>
                    <Select
                      name="team"
                      value={formData.team}
                      onChange={handleSelectChange}
                      label="Team"
                    >
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      {TEAMS.map((team) => (
                        <MenuItem key={team.value} value={team.value}>
                          <Chip 
                            label={team.label} 
                            size="small"
                            sx={{
                              bgcolor: team.bgColor,
                              color: team.color,
                              fontWeight: 600
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridLegacy>

                <GridLegacy size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    placeholder="Internal notes about this user..."
                    helperText="Private notes for management use"
                  />
                </GridLegacy>
              </>
            )}
          </GridLegacy>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={!formData.username || (!editMode && (!formData.pin || !formData.attribution_id))}
            sx={{
              bgcolor: '#4B88A2',
              '&:hover': {
                bgcolor: '#3d7088'
              },
              fontWeight: 600
            }}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default UserManagement;
